import { AtlBrowserStorageLocal, AtlBrowserStorageSession } from '@atlassian/browser-storage-controls/web-storage-service';

import { fg } from '@confluence/feature-gating';

import { StorageManager } from './StorageManager.ts';
import {
	STORAGE_MANAGER_PREFIX,
	STORAGE_MANAGER_ID,
	LOCAL_STORAGE_MANAGER,
	SESSION_STORAGE_MANAGER,
} from './storageKeys.ts';
import { ssrLocalStorage, ssrSessionStorage } from './SSRStorage.ts';
import type { StorageManagerInitContext } from './StorageManagerInitContext.ts';
import type { AtlBrowserStorage } from './StorageManager.ts';
import { getRemoteLocalStorage } from './getRemoteLocalStorage.ts';
import { registerSearchClientStorageAdapterForConfluence } from './registerSearchClientStorageAdapter.ts';

// check if local and session storage are valid
const isStorageValid = (storage: any): storage is Storage => {
	try {
		return (
			typeof storage !== 'undefined' &&
			'setItem' in storage &&
			// in IE8 typeof localStorage.setItem === 'object'
			!!storage.setItem
		);
	} catch {
		return false;
	}
};

const getStorage = (type: 'localStorage' | 'sessionStorage'): Storage | null => {
	if (typeof window === 'object' && isStorageValid(window[type])) {
		return window[type];
	}
	return null;
};

export let confluenceLocalStorageInstance: StorageManager;
export let confluenceSessionStorageInstance: StorageManager;

export const initializeStorageManager = async (initializationData: StorageManagerInitContext) => {
	let confluenceLocalStorage: Storage | AtlBrowserStorage | null = null;
	let confluenceSessionStorage: Storage | AtlBrowserStorage | null = null;
	let confluenceLocalReadStorage: Storage | null = null;
	let confluenceSessionReadStorage: Storage | null = null;

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR) {
		confluenceLocalStorage = ssrLocalStorage;
		confluenceSessionStorage = ssrSessionStorage;
		ssrLocalStorage.setConfluenceBrowserStorageControls(
			initializationData.confluenceBrowserStorageControls,
		);
		ssrSessionStorage.setConfluenceBrowserStorageControls(
			initializationData.confluenceBrowserStorageControls,
		);

		if (
			initializationData.confluenceBrowserStorageControls ??
			fg('confluence_browser_storage_controls')
		) {
			const preferences = window.__PRELOADED_COOKIE_PREFERENCES__;

			ssrLocalStorage.setCookieCategories(window.__WEB_STORAGE_CONSENT_CATEGORIES_LOCAL__ ?? null);
			ssrLocalStorage.setPreferences(preferences);
			ssrSessionStorage.setCookieCategories(
				window.__WEB_STORAGE_CONSENT_CATEGORIES_SESSION__ ?? null,
			);
			ssrSessionStorage.setPreferences(preferences);
		}
	} else if (fg('confluence_browser_storage_controls')) {
		confluenceLocalStorage = AtlBrowserStorageLocal;
		confluenceSessionStorage = AtlBrowserStorageSession;
		confluenceLocalReadStorage = AtlBrowserStorageLocal.storage;
		confluenceSessionReadStorage = AtlBrowserStorageSession.storage;
	} else {
		confluenceLocalStorage = getStorage('localStorage');
		confluenceSessionStorage = getStorage('sessionStorage');
	}

	confluenceLocalStorageInstance = new StorageManager(
		confluenceLocalStorage,
		STORAGE_MANAGER_PREFIX,
		STORAGE_MANAGER_ID,
		LOCAL_STORAGE_MANAGER,
		initializationData,
		confluenceLocalReadStorage,
	);

	confluenceSessionStorageInstance = new StorageManager(
		confluenceSessionStorage,
		STORAGE_MANAGER_PREFIX,
		STORAGE_MANAGER_ID,
		SESSION_STORAGE_MANAGER,
		initializationData,
		confluenceSessionReadStorage,
	);

	const result = await getRemoteLocalStorage(initializationData);
	if (result) {
		confluenceLocalStorageInstance.initializeFromServer(result);
	}

	// Register the search-client storage adapter for combined config SSR persistence.
	// This must be called after storage instances are created.
	// We pass a getter function to avoid circular dependencies.
	registerSearchClientStorageAdapterForConfluence(() => confluenceLocalStorageInstance);
};
